import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Card from '../../common/Card';
import Button from '../../common/Button';
import { eventService } from '../../../services/firebase/eventService';
import './Events.css';

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const upcomingEvents = await eventService.getUpcoming(3);
      setEvents(upcomingEvents);
    } catch (err) {
      console.error('Error loading events:', err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const eventImages = {
    'workshop': 'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?w=600&h=400&q=80&fit=crop',
    'training': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&h=400&q=80&fit=crop',
    'class': 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&h=400&q=80&fit=crop',
    'retreat': 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=600&h=400&q=80&fit=crop',
    'default': 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=600&h=400&q=80&fit=crop'
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const options = { month: 'long', day: 'numeric', year: 'numeric' };
    return d.toLocaleDateString('en-AU', options);
  };

  const getStatusBadge = (event) => {
    if (event.status === 'full' || event.spots?.available <= 0) return 'Full';
    if (event.status === 'filling-fast' || event.spots?.available <= 3) return 'Filling Fast';
    return `${event.spots?.available || 0} Spots Left`;
  };

  const getStatusClass = (event) => {
    if (event.status === 'full' || event.spots?.available <= 0) return 'event-badge--full';
    if (event.status === 'filling-fast' || event.spots?.available <= 3) return 'event-badge--filling';
    return 'event-badge--open';
  };

  const handleBookNow = (eventId) => {
    navigate(`/events`);
  };

  // Don't render the section if no events are available
  if (!loading && events.length === 0) {
    return null;
  }

  return (
    <section className="section events bg-light-beige">
      <div className="container">
        <div className="events__header text-center">
          <h2>Upcoming Events & Workshops</h2>
          <p className="events__subtitle">
            Join our community gatherings, workshops, and transformative experiences
          </p>
        </div>

        {loading ? (
          <div className="events__loading">
            <div className="spinner"></div>
            <p>Loading upcoming events...</p>
          </div>
        ) : (
          <div className="events__grid">
            {events.map((event) => (
              <Card key={event.id} className="event-card">
                <div className="event-card__image">
                  <img
                    src={event.imageUrl || eventImages[event.category?.toLowerCase()] || eventImages.default}
                    alt={event.title}
                    className="event-card__img"
                  />
                </div>
                <div className="event-card__content">
                  <div className="event-card__header">
                    <span className={`event-badge ${getStatusClass(event)}`}>
                      {getStatusBadge(event)}
                    </span>
                    {event.category && (
                      <span className="event-category">{event.category}</span>
                    )}
                  </div>
                  <div className="event-card__date">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"/>
                    </svg>
                    <span>{formatDate(event.date)}</span>
                  </div>
                  <h3 className="event-card__title">{event.title}</h3>
                  <p className="event-card__time">
                    {event.time}
                    {event.endTime && ` - ${event.endTime}`}
                  </p>
                  {event.description && (
                    <p className="event-card__description">
                      {event.description.length > 150
                        ? `${event.description.substring(0, 150)}...`
                        : event.description}
                    </p>
                  )}
                  <div className="event-card__footer">
                    <span className="event-card__price">
                      {event.price === 0 ? 'Free' : `$${event.price} AUD`}
                    </span>
                    <Button
                      variant={event.status === 'full' || event.spots?.available <= 0 ? 'secondary' : 'primary'}
                      size="small"
                      disabled={event.status === 'full' || event.spots?.available <= 0}
                      onClick={() => handleBookNow(event.id)}
                    >
                      {event.status === 'full' || event.spots?.available <= 0 ? 'Full' : 'Book Now'}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="events__cta text-center">
          <Link to="/events">
            <Button variant="secondary" size="large">
              View All Events
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Events;
